import React, { useState, useEffect } from 'react';
import { MessageCircle, CheckCircle, EyeOff, Trash2, Ban, UserX, Users, Search, Shield, ShieldCheck, ShieldOff, Mail, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 50 });
      if (userSearch) params.set('search', userSearch);
      const res = await fetch(`${API_URL}/api/cms/users?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setUsers(d.users); setUsersTotal(d.total); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchComments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 50 });
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API_URL}/api/cms/comments?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setComments(d.comments); setCommentsTotal(d.total); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchBlocked = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/blocked-users`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setBlockedUsers(d.blocked_users); }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (tab === 'users') fetchUsers();
    if (tab === 'comments') fetchComments();
    if (tab === 'blocked') fetchBlocked();
  }, [tab, statusFilter]);

  // User actions
  const handleVerify = async (id) => {
    await fetch(`${API_URL}/api/cms/users/${id}/verify`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    fetchUsers();
  };
  const handleUnverify = async (id) => {
    await fetch(`${API_URL}/api/cms/users/${id}/unverify`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    fetchUsers();
  };
  const handleDeleteUser = async (id) => {
    if (!window.confirm('Delete this user and all their activity? This cannot be undone.')) return;
    await fetch(`${API_URL}/api/cms/users/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchUsers();
  };
  const handleBlockFromUsers = async (id) => {
    if (!window.confirm('Block this user?')) return;
    await fetch(`${API_URL}/api/cms/comments/block-user?reader_id=${id}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    fetchUsers(); fetchBlocked();
  };

  // Comment actions
  const handleApprove = async (id) => {
    await fetch(`${API_URL}/api/cms/comments/${id}/approve`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    fetchComments();
  };
  const handleHide = async (id) => {
    await fetch(`${API_URL}/api/cms/comments/${id}/hide`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    fetchComments();
  };
  const handleDeleteComment = async (id) => {
    if (!window.confirm('Delete this comment permanently?')) return;
    await fetch(`${API_URL}/api/cms/comments/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchComments();
  };
  const handleBlockFromComment = async (readerId) => {
    if (!window.confirm('Block this user? All their comments will be hidden.')) return;
    await fetch(`${API_URL}/api/cms/comments/block-user?reader_id=${readerId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    fetchComments(); fetchBlocked();
  };
  const handleUnblock = async (readerId) => {
    await fetch(`${API_URL}/api/cms/comments/block-user?reader_id=${readerId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchBlocked();
  };

  const handleUserSearch = (e) => { e.preventDefault(); fetchUsers(); };

  const statusColors = { approved: 'bg-green-500/10 text-green-400', hidden: 'bg-red-500/10 text-red-300', pending: 'bg-amber-500/10 text-amber-300' };

  return (
    <div data-testid="admin-users">
      <div className="mb-6">
        <h1 className="font-serif text-2xl text-cream font-semibold">Users & Moderation</h1>
        <p className="text-cream/30 text-sm mt-1">Manage users, verify accounts, moderate comments</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-navy border border-gold/[0.06] rounded-lg p-1 mb-6 w-fit">
        {[
          { id: 'users', label: 'Users', icon: Users, count: usersTotal },
          { id: 'comments', label: 'Comments', icon: MessageCircle, count: commentsTotal },
          { id: 'blocked', label: 'Blocked', icon: UserX, count: blockedUsers.length },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${tab === t.id ? 'bg-gold/[0.1] text-gold' : 'text-cream/30 hover:text-cream/50'}`} data-testid={`tab-${t.id}`}>
            <t.icon size={12} /> {t.label} {t.count > 0 && <span className="text-[9px] bg-cream/[0.05] px-1.5 py-0.5 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ═══ USERS TAB ═══ */}
      {tab === 'users' && (
        <>
          <form onSubmit={handleUserSearch} className="mb-4 max-w-xs">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/20" />
              <input type="text" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search users..." className="w-full bg-navy border border-gold/[0.06] rounded-lg pl-9 pr-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:border-gold/20" data-testid="user-search" />
            </div>
          </form>

          {loading ? <div className="py-12 text-center text-cream/20">Loading...</div> : users.length === 0 ? (
            <div className="py-12 text-center bg-navy border border-dashed border-gold/[0.06] rounded-xl"><Users size={28} className="mx-auto text-cream/10 mb-2" /><p className="text-cream/25 text-sm">No users found</p></div>
          ) : (
            <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gold/[0.04]">
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20">User</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden md:table-cell">Joined</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20">Status</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20">Actions</th>
                </tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-gold/[0.03] hover:bg-cream/[0.01] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gold/[0.08] rounded-full flex items-center justify-center text-gold/50 text-xs font-bold flex-shrink-0">
                            {(u.first_name || u.email || 'U')[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-cream/70 text-sm font-medium">{u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.email?.split('@')[0]}</span>
                              {u.verified && <CheckCircle size={12} className="text-blue-400" />}
                            </div>
                            <span className="text-cream/20 text-[10px]">{u.email}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-cream/20 text-xs hidden md:table-cell">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</td>
                      <td className="px-4 py-3">
                        {u.verified ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-300"><ShieldCheck size={10} /> Verified</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-cream/[0.03] text-cream/20">Unverified</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          {!u.verified ? (
                            <button onClick={() => handleVerify(u.id)} className="p-1.5 text-cream/15 hover:text-blue-400 transition-colors" title="Verify"><ShieldCheck size={14} /></button>
                          ) : (
                            <button onClick={() => handleUnverify(u.id)} className="p-1.5 text-cream/15 hover:text-amber-400 transition-colors" title="Remove verification"><ShieldOff size={14} /></button>
                          )}
                          <button onClick={() => handleBlockFromUsers(u.id)} className="p-1.5 text-cream/15 hover:text-red-400 transition-colors" title="Block"><Ban size={14} /></button>
                          <button onClick={() => handleDeleteUser(u.id)} className="p-1.5 text-cream/15 hover:text-red-500 transition-colors" title="Delete"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══ COMMENTS TAB ═══ */}
      {tab === 'comments' && (
        <>
          <div className="flex gap-3 mb-4">
            <div className="flex gap-1 bg-navy border border-gold/[0.06] rounded-lg p-1">
              {['', 'approved', 'hidden'].map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1 rounded text-xs font-medium transition-all ${statusFilter === s ? 'bg-gold/[0.1] text-gold' : 'text-cream/30 hover:text-cream/50'}`}>{s || 'All'}</button>
              ))}
            </div>
            <span className="text-cream/15 text-xs self-center">{commentsTotal} comments</span>
          </div>

          {loading ? <div className="py-12 text-center text-cream/20">Loading...</div> : comments.length === 0 ? (
            <div className="py-12 text-center bg-navy border border-dashed border-gold/[0.06] rounded-xl"><MessageCircle size={28} className="mx-auto text-cream/10 mb-2" /><p className="text-cream/25 text-sm">No comments</p></div>
          ) : (
            <div className="space-y-2">
              {comments.map((c) => (
                <div key={c.id} className="bg-navy border border-gold/[0.06] rounded-xl p-4 hover:border-gold/[0.1] transition-all" data-testid={`comment-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 bg-gold/[0.08] rounded-full flex items-center justify-center text-gold/50 text-[10px] font-bold">{(c.reader_name || 'R')[0].toUpperCase()}</div>
                        <span className="text-cream/60 text-xs font-medium">{c.reader_name || 'Anonymous'}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${statusColors[c.status] || 'bg-cream/5 text-cream/20'}`}>{c.status}</span>
                        <span className="text-cream/10 text-[10px]">{new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p className="text-cream/40 text-sm mb-1">{c.content}</p>
                      <p className="text-cream/15 text-[10px]">on: {c.article_slug}</p>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {c.status !== 'approved' && <button onClick={() => handleApprove(c.id)} className="p-1.5 text-cream/15 hover:text-green-400 transition-colors" title="Approve"><CheckCircle size={14} /></button>}
                      {c.status !== 'hidden' && <button onClick={() => handleHide(c.id)} className="p-1.5 text-cream/15 hover:text-amber-400 transition-colors" title="Hide"><EyeOff size={14} /></button>}
                      <button onClick={() => handleDeleteComment(c.id)} className="p-1.5 text-cream/15 hover:text-red-400 transition-colors" title="Delete"><Trash2 size={14} /></button>
                      <button onClick={() => handleBlockFromComment(c.reader_id)} className="p-1.5 text-cream/15 hover:text-red-500 transition-colors" title="Block user"><Ban size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ BLOCKED TAB ═══ */}
      {tab === 'blocked' && (
        blockedUsers.length === 0 ? (
          <div className="py-12 text-center bg-navy border border-dashed border-gold/[0.06] rounded-xl"><UserX size={28} className="mx-auto text-cream/10 mb-2" /><p className="text-cream/25 text-sm">No blocked users</p></div>
        ) : (
          <div className="space-y-2">
            {blockedUsers.map((u) => (
              <div key={u.reader_id} className="bg-navy border border-gold/[0.06] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-cream/50 text-sm">Reader: {u.reader_id.slice(0, 12)}...</p>
                  <p className="text-cream/15 text-[10px]">Blocked by {u.blocked_by} on {new Date(u.blocked_at).toLocaleDateString('en-GB')}</p>
                </div>
                <button onClick={() => handleUnblock(u.reader_id)} className="px-3 py-1.5 text-xs text-cream/30 border border-cream/10 rounded-lg hover:border-green-500/30 hover:text-green-400 transition-all">Unblock</button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default AdminUsers;
